import type { BatchNpiLookupResult, FoundNpiFacility } from "@services/facilityNetworkLink";
import type { ChildFacilitySearchHit } from "./addChildFacilitySearchUtils";

export function countProcessedNpis(review: BatchNpiLookupResult): number {
  return review.found.length + review.not_found.length + review.invalid.length;
}

export function isAllFoundReview(review: BatchNpiLookupResult): boolean {
  return review.found.length > 0 && review.not_found.length === 0 && review.invalid.length === 0;
}

export function formatReviewProcessedSubtitle(review: BatchNpiLookupResult): string {
  const processed = countProcessedNpis(review);
  const noun = processed === 1 ? "NPI" : "NPIs";
  if (isAllFoundReview(review)) {
    return `${processed} ${noun} processed — all found in Unicomply`;
  }
  return `${processed} ${noun} processed · Review before sending`;
}

export function canConfirmReview(foundCount: number, notFoundCount: number): boolean {
  return foundCount > 0 || notFoundCount > 0;
}

export function formatLinkRequestWillSend(count: number): string {
  return count === 1 ? "link request will be sent" : "link requests will be sent";
}

export function formatFollowUpSaved(count: number): string {
  return count === 1 ? "facility saved for follow-up" : "facilities saved for follow-up";
}

export function formatLinkRequestsSent(count: number): string {
  return count === 1 ? "1 link request sent" : `${count} link requests sent`;
}

export function formatProcessedNpiCount(count: number): string {
  const noun = count === 1 ? "NPI" : "NPIs";
  return `${count} ${noun} processed`;
}

export function formatConfirmOutcomeLine(
  sent: number,
  saved: number,
  skipped: number,
): string {
  return `${formatLinkRequestsSent(sent)} · ${saved} saved for follow-up · ${skipped} skipped`;
}

export function formatInviteNextStepTitle(count: number): string {
  if (count === 1) return "Next step —  1 facility needs a Unicomply invite";
  return `Next step —  ${count} facilities need a Unicomply invite`;
}

export function hitByNpi(
  hits: ChildFacilitySearchHit[],
): Map<string, ChildFacilitySearchHit> {
  const map = new Map<string, ChildFacilitySearchHit>();
  for (const hit of hits) {
    for (const key of [hit.npi, hit.ccn, hit.registryNpi]) {
      const k = (key || "").trim();
      if (k && !map.has(k)) map.set(k, hit);
    }
  }
  return map;
}

function displayNpiFromHit(
  hit?: ChildFacilitySearchHit,
  fallback?: string,
): string {
  const registry = (hit?.registryNpi || "").trim();
  if (registry && /^\d{10}$/.test(registry)) return registry;
  const lookup = (hit?.npi || "").trim();
  const ccn = (hit?.ccn || "").trim();
  if (lookup && lookup !== ccn && /^\d{10}$/.test(lookup)) return lookup;
  const fb = (fallback || "").trim();
  if (fb && fb !== ccn && /^\d{10}$/.test(fb)) return fb;
  return "";
}

export function formatFoundPrimaryLine(
  item: FoundNpiFacility,
  hit?: ChildFacilitySearchHit,
): string {
  const name = (item.facility_name || hit?.name || item.npi).trim();
  const ccn = (hit?.ccn || "").trim();
  const npi = displayNpiFromHit(hit, item.npi);
  const parts = [name, `CCN ${ccn || "NA"}`];
  if (npi) parts.push(`NPI ${npi}`);
  return parts.join(" · ");
}

export function formatFoundSecondaryLine(
  item: FoundNpiFacility,
  hit?: ChildFacilitySearchHit,
): string {
  const type = (hit?.facilityType || "").trim();
  const npi = displayNpiFromHit(hit, item.npi) || "NA";
  return type ? `NPI ${npi} · ${type}` : `NPI ${npi}`;
}

export function formatNotFoundPrimaryLine(
  npi: string,
  hit?: ChildFacilitySearchHit,
  registryName?: string | null,
): string {
  const name = (hit?.name || registryName || "").trim();
  const ccn = (hit?.ccn || "").trim();
  const displayNpi = displayNpiFromHit(hit, npi);
  if (name && name !== npi && !/^NPI\s+/i.test(name) && !/^CCN\s*\/\s*NPI/i.test(name) && !/^Facility\s*\(/i.test(name)) {
    const parts = [name, `CCN ${ccn || "NA"}`];
    if (displayNpi && displayNpi !== ccn) parts.push(`NPI ${displayNpi}`);
    return parts.join(" · ");
  }
  if (ccn && displayNpi && displayNpi !== ccn) {
    return `CCN ${ccn} · NPI ${displayNpi}`;
  }
  return `CCN / NPI ${npi}`;
}

export function formatWontSendPrimaryLine(
  identifier: string,
  hit?: ChildFacilitySearchHit,
  registryName?: string | null,
): string {
  const id = (identifier || "").trim();
  const name = (hit?.name || registryName || "").trim();
  const ccn = (hit?.ccn || "").trim();
  const displayNpi = displayNpiFromHit(hit, id);
  if (name && name !== id && !/^NPI\s+/i.test(name) && !/^CCN\s+/i.test(name) && !/^Facility\s*\(/i.test(name)) {
    const parts = [name, `CCN ${ccn || "NA"}`];
    if (displayNpi && displayNpi !== ccn) parts.push(`NPI ${displayNpi}`);
    return parts.join(" · ");
  }
  return id ? `CCN ${id}` : "Unknown";
}

export function hitsFromFacilityNameByNpi(
  facilityNameByNpi?: Record<string, string> | null,
): ChildFacilitySearchHit[] {
  if (!facilityNameByNpi) return [];
  return Object.entries(facilityNameByNpi)
    .map(([npi, name]) => {
      const trimmedNpi = (npi || "").trim();
      const trimmedName = (name || "").trim();
      if (!trimmedNpi || !trimmedName) return null;
      return {
        npi: trimmedNpi,
        name: trimmedName,
        city: "",
        state: "",
        ccn: "",
      } satisfies ChildFacilitySearchHit;
    })
    .filter((hit): hit is ChildFacilitySearchHit => hit != null);
}

export function isUsableReviewFacilityName(
  name?: string | null,
  npi?: string | null,
): boolean {
  const trimmed = (name || "").trim();
  if (!trimmed) return false;
  const id = (npi || "").trim();
  if (id && trimmed === id) return false;
  if (/^NPI\s+/i.test(trimmed)) return false;
  if (/^CCN\s*\/\s*NPI/i.test(trimmed)) return false;
  if (/^CCN\s+\d+$/i.test(trimmed)) return false;
  if (/^Facility\s*\(/i.test(trimmed)) return false;
  return true;
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/** NPIs/CCNs in invite or won't-send buckets that still lack a usable display name. */
export function identifiersNeedingRegistryName(review: BatchNpiLookupResult): string[] {
  const names = review.facility_name_by_npi || {};
  const missing = new Set<string>();
  const consider = (raw: string, status?: string) => {
    if (status === "invalid") return;
    const id = (raw || "").trim();
    if (!id) return;
    const key = digitsOnly(id);
    if (key.length < 6 || key.length > 10) return;
    if (
      isUsableReviewFacilityName(names[id], id) ||
      isUsableReviewFacilityName(names[key], key)
    ) {
      return;
    }
    missing.add(key.length >= 10 ? key.slice(-10) : key);
  };
  (review.save_for_invite || review.not_found).forEach((npi) => consider(npi));
  (review.wont_send || []).forEach((row) => consider(row.identifier, row.status));
  return Array.from(missing);
}

export async function enrichBatchReviewFacilityNames(
  review: BatchNpiLookupResult,
  lookupHit: (npi: string) => Promise<ChildFacilitySearchHit | null>,
): Promise<{ review: BatchNpiLookupResult; hits: ChildFacilitySearchHit[] }> {
  const nameByNpi: Record<string, string> = { ...(review.facility_name_by_npi || {}) };
  const hits: ChildFacilitySearchHit[] = [];
  const missing = identifiersNeedingRegistryName(review);
  if (missing.length === 0) {
    return {
      review: { ...review, facility_name_by_npi: nameByNpi },
      hits: hitsFromFacilityNameByNpi(nameByNpi),
    };
  }
  await Promise.all(
    missing.map(async (npi) => {
      try {
        const hit = await lookupHit(npi);
        if (!hit || !isUsableReviewFacilityName(hit.name, npi)) return;
        nameByNpi[npi] = hit.name.trim();
        hits.push(hit);
      } catch {
        /* keep placeholder */
      }
    }),
  );
  return {
    review: { ...review, facility_name_by_npi: nameByNpi },
    hits,
  };
}
